import React, { useEffect, useState, useRef } from "react";
import { CheckCircle, NavigateNext, NavigateBefore } from "@mui/icons-material";
import {
	Container,
	CssBaseline,
	Paper,
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Slider,
	Stack,
	IconButton
} from "@mui/material";
import { useStore } from "../../contexts";

interface AvailableWebcamsProps {
	label: string;
	isActive: boolean;
	onNext: () => void;
	onPrev: () => void;
	isFirst: boolean;
	isLast: boolean;
}
type Dot = { x: number; y: number };

type DotSettings = { dot1: Dot; dot2: Dot };

const AvailableWebcams: React.FC<AvailableWebcamsProps> = ({
	label,
	isActive,
	onNext,
	onPrev,
	isFirst,
	isLast
}: AvailableWebcamsProps) => {
	const { setCameraSettings, storeData } = useStore();
	const [webcamDevices, setWebcamDevices] = useState<MediaDeviceInfo[]>([]);
	const [selectedWebcam, setSelectedWebcam] = useState<MediaDeviceInfo | undefined>(undefined);
	const [lastFrame, setLastFrame] = useState<string | null>(null);

	const [horizentalValue, setHorizentalValue] = useState<number[]>(
		storeData?.cameraSettings[label + "HorizentalValue"] || [0, 100]
	);
	const [verticalValue, setVerticalValue] = useState<number[]>(
		storeData?.cameraSettings[label + "VerticalValue"] || [0, 100]
	);

	const videoRef = useRef<HTMLVideoElement | null>(null);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const [dots, setDots] = useState<DotSettings>({
		dot1: { x: 0, y: 0 },
		dot2: { x: 0, y: 0 }
	});
	const [currentDot, setCurrentDot] = useState<"dot1" | "dot2">("dot1");
	const streamRef = useRef<MediaStream | null>(null); // Ref to store the current stream

	const init = async () => {
		try {
			const devices = await navigator.mediaDevices.enumerateDevices();
			const videoDevices = devices.filter((device) => device.kind === "videoinput");
			setWebcamDevices(videoDevices);

			// Find the selected device based on stored deviceId
			const storedDeviceId = storeData?.cameraSettings[label];
			if (storedDeviceId) {
				const foundDevice = videoDevices.find((device) => device.deviceId === storedDeviceId);
				if (foundDevice) {
					setSelectedWebcam(foundDevice);
					// Don't start stream here, let the selectedWebcam useEffect handle it
				}
			}
		} catch (error) {
			console.error("Error accessing webcam:", error);
		}
	};

	const startStream = async (device: MediaDeviceInfo) => {
		try {
			// Stop any existing stream
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((track) => track.stop());
			}

			// Only start stream if this camera is active
			if (!isActive) return;

			console.log(`Starting stream for ${label} (active: ${isActive})`);
			const stream = await navigator.mediaDevices.getUserMedia({
				video: {
					deviceId: { exact: device.deviceId },
					width: storeData?.cameraSettings.width,
					height: storeData?.cameraSettings.height
				}
			});

			streamRef.current = stream; // Store the stream in ref

			if (videoRef.current) {
				videoRef.current.srcObject = stream;

				// Wait for video to load and capture initial frame
				videoRef.current.onloadedmetadata = () => {
					console.log(
						`Video loaded for ${label}, dimensions:`,
						videoRef.current?.videoWidth,
						videoRef.current?.videoHeight
					);
					// Capture initial frame after a short delay
					setTimeout(() => {
						captureLastFrame();
					}, 500);
				};
			}
		} catch (error) {
			console.error("Error accessing webcam:", error);
		}
	};

	const captureLastFrame = () => {
		if (videoRef.current && canvasRef.current && videoRef.current.videoWidth > 0) {
			const canvas = canvasRef.current;
			const video = videoRef.current;
			const ctx = canvas.getContext("2d");

			if (ctx) {
				canvas.width = video.videoWidth;
				canvas.height = video.videoHeight;
				ctx.drawImage(video, 0, 0);
				const dataURL = canvas.toDataURL("image/jpeg", 0.8);
				setLastFrame(dataURL);
				console.log("Last frame captured for", label);
			}
		} else {
			console.log(
				"Cannot capture frame for",
				label,
				"video dimensions:",
				videoRef.current?.videoWidth,
				videoRef.current?.videoHeight
			);
		}
	};

	// Capture frame periodically when active
	useEffect(() => {
		let intervalId: NodeJS.Timeout;

		if (isActive && selectedWebcam && videoRef.current) {
			// Capture frame every 2 seconds when active
			intervalId = setInterval(() => {
				captureLastFrame();
			}, 2000);
		}

		return () => {
			if (intervalId) {
				clearInterval(intervalId);
			}
		};
	}, [isActive, selectedWebcam]);

	const getDots = () => {
		if (!videoRef.current) return;
		const rect = videoRef.current.getBoundingClientRect();

		const dot1 = {
			x: (storeData?.cameraSettings[label + "DOT1"][0] / 100) * rect.width,
			y: (storeData?.cameraSettings[label + "DOT1"][1] / 100) * rect.height
		};

		const dot2 = {
			x: (storeData?.cameraSettings[label + "DOT2"][0] / 100) * rect.width,
			y: (storeData?.cameraSettings[label + "DOT2"][1] / 100) * rect.height
		};

		setDots({ dot1, dot2 });
	};

	useEffect(() => {
		init();
	}, [label]); // Only depend on label, not selectedWebcam

	useEffect(() => {
		// Handle selectedWebcam changes separately
		if (selectedWebcam) {
			if (isActive) {
				startStream(selectedWebcam);
			} else {
				// If becoming inactive, try to capture last frame before stopping
				setTimeout(() => {
					captureLastFrame();
				}, 100); // Small delay to ensure video is ready

				if (streamRef.current) {
					streamRef.current.getTracks().forEach((track) => track.stop());
					streamRef.current = null;
				}
			}
		}
		return () => {
			console.log("Cleaning up: Stopping webcam for", label);
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((track) => track.stop());
				streamRef.current = null;
			}
			if (videoRef.current) {
				videoRef.current.srcObject = null;
			}
		};
	}, [selectedWebcam, label, isActive]);

	useEffect(() => {
		if (videoRef.current) {
			getDots();
		}
	}, [videoRef.current]);

	const handleWebcamChange = async (event: React.ChangeEvent<{ value: unknown }>) => {
		const selectedDeviceId = event.target.value;
		const selected = webcamDevices.find((device) => device.deviceId === selectedDeviceId);

		if (selected) {
			setSelectedWebcam(selected);
			// Store only the deviceId instead of the full MediaDeviceInfo object
			setCameraSettings({ [label]: selected.deviceId });

			await startStream(selected);
		}
	};

	const handleChangeHorizental = (event: Event, newValue: number | number[], activeThumb: number) => {
		if (!Array.isArray(newValue)) {
			return;
		}
		let value: number[];
		if (activeThumb === 0) {
			value = [Math.min(newValue[0], horizentalValue[1] - 10), horizentalValue[1]];
		} else {
			value = [horizentalValue[0], Math.max(newValue[1], horizentalValue[0] + 10)];
		}
		setHorizentalValue(value);
		console.log(label + "HorizentalValue", value);
		setCameraSettings({ [label + "HorizentalValue"]: value });
	};

	const handleChangeVertical = (event: Event, newValue: number | number[], activeThumb: number) => {
		if (!Array.isArray(newValue)) {
			return;
		}
		let value: number[];
		if (activeThumb === 0) {
			value = [Math.min(newValue[0], verticalValue[1] - 10), verticalValue[1]];
		} else {
			value = [verticalValue[0], Math.max(newValue[1], verticalValue[0] + 10)];
		}
		setVerticalValue(value);
		console.log(label + "VerticalValue", value);
		setCameraSettings({ [label + "VerticalValue"]: value });
	};

	const handleClick = (event: React.MouseEvent<HTMLVideoElement, MouseEvent>) => {
		if (!videoRef.current) return;
		const rect = videoRef.current.getBoundingClientRect();

		const x = event.clientX - rect.left;
		const y = event.clientY - rect.top;

		const percentageX = (x / rect.width) * 100;
		const percentageY = (y / rect.height) * 100;

		setDots((prevDots) => ({
			...prevDots,
			[currentDot]: { x, y }
		}));

		setCameraSettings({ [label + currentDot.toUpperCase()]: [percentageX, percentageY] });

		// Toggle between dot1 and dot2
		setCurrentDot((prevDot) => (prevDot === "dot1" ? "dot2" : "dot1"));
	};

	return (
		<div className={`w-[50%] ${!isActive ? "opacity-50 pointer-events-none" : ""}`}>
			<div className="flex items-center mx-[5%] relative">
				<div className="text-2xl mr-[5%] min-w-[20%]">{label.toUpperCase()}:</div>
				<FormControl className="flex-1" variant="outlined">
					<InputLabel>Choose Webcam</InputLabel>
					<Select
						value={selectedWebcam?.deviceId || ""}
						onChange={handleWebcamChange as any}
						label={`${label} Webcam`}
						color="success"
						disabled={!isActive}
						renderValue={(selected) => {
							if (!selected) return "Choose the desired camera";
							const device = webcamDevices.find((d) => d.deviceId === selected);
							return device ? device.label : "Choose the desired camera";
						}}
					>
						<MenuItem value="">
							<em>Choose the desired camera</em>
						</MenuItem>
						{webcamDevices.map((device, index) => (
							<MenuItem key={index} value={device.deviceId}>
								{device.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				{selectedWebcam && <CheckCircle className="text-primary" />}

				{/* Navigation buttons */}
				<div className="flex ml-4">
					<IconButton onClick={onPrev} disabled={isFirst} color="primary" size="large">
						<NavigateBefore />
					</IconButton>
					<IconButton onClick={onNext} disabled={isLast} color="primary" size="large">
						<NavigateNext />
					</IconButton>
				</div>
			</div>
			{selectedWebcam && (
				<Stack sx={{ height: "auto", margin: "5%" }}>
					<Stack sx={{ height: "30vh" }} justifyContent={"center"} direction="row">
						<Slider
							orientation="vertical"
							value={verticalValue}
							sx={{ marginRight: 2, marginTop: 5 }}
							onChange={handleChangeVertical}
							valueLabelDisplay="auto"
							disabled={!isActive}
						/>
						<div>
							<Slider
								value={horizentalValue}
								valueLabelDisplay="auto"
								onChange={handleChangeHorizental}
								disabled={!isActive}
							/>
							<div className="h-[30vh] w-[40vh] m-2 overflow-hidden relative">
								{isActive ? (
									<video
										ref={videoRef}
										autoPlay
										playsInline
										onClick={handleClick}
										style={{
											width: "100%",
											height: "100%",
											cursor: isActive ? "crosshair" : "default"
										}}
									/>
								) : lastFrame ? (
									<img
										src={lastFrame}
										alt="Last frame"
										style={{
											width: "100%",
											height: "100%",
											objectFit: "cover"
										}}
									/>
								) : (
									<div
										style={{
											width: "100%",
											height: "100%",
											backgroundColor: "#f0f0f0",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											color: "#666"
										}}
									>
										No preview available
									</div>
								)}
								<canvas ref={canvasRef} style={{ display: "none" }} />
								{dots.dot1.x !== 0 && dots.dot1.y !== 0 && (
									<div
										style={{
											position: "absolute",
											width: "10px",
											height: "10px",
											borderRadius: "50%",
											backgroundColor: "red",
											top: `${dots.dot1.y - 5}px`,
											left: `${dots.dot1.x - 5}px`,
											pointerEvents: "none"
										}}
									></div>
								)}
								{dots.dot2.x !== 0 && dots.dot2.y !== 0 && (
									<div
										style={{
											position: "absolute",
											width: "10px",
											height: "10px",
											borderRadius: "50%",
											backgroundColor: "blue",
											top: `${dots.dot2.y - 5}px`,
											left: `${dots.dot2.x - 5}px`,
											pointerEvents: "none"
										}}
									></div>
								)}
								{/* Overlay Bars */}
								<div
									id="topBar"
									style={{
										position: "absolute",
										top: 0,
										height: `${(100 - verticalValue[1]) * 0.3}vh`,
										backgroundColor: "rgba(0,0,0,0.5)",
										width: "100%",
										zIndex: 1
									}}
								></div>
								<div
									id="leftBar"
									style={{
										position: "absolute",
										top: 0,
										left: 0,
										height: "100%",
										backgroundColor: "rgba(0,0,0,0.5)",
										width: `${horizentalValue[0] * 0.4}vh`,
										zIndex: 1
									}}
								></div>
								<div
									id="rightBar"
									style={{
										position: "absolute",
										top: 0,
										right: 0,
										height: "100%",
										backgroundColor: "rgba(0,0,0,0.5)",
										width: `${(100 - horizentalValue[1]) * 0.4}vh`,
										zIndex: 1
									}}
								></div>
								<div
									id="bottomBar"
									style={{
										position: "absolute",
										bottom: 0,
										height: `${verticalValue[0] * 0.3}vh`,
										backgroundColor: "rgba(0,0,0,0.5)",
										width: "100%",
										zIndex: 1
									}}
								></div>
							</div>
						</div>
					</Stack>
				</Stack>
			)}
		</div>
	);
};

interface IntegratedComponentProps {
	handleCloseSettingsDialog: () => void;
}

const IntegratedComponent: React.FC<IntegratedComponentProps> = ({ handleCloseSettingsDialog }) => {
	const [activeCameraIndex, setActiveCameraIndex] = useState(0);
	const cameraLabels = ["leftTop", "rightTop", "leftSide", "rightSide"];

	const handleNext = () => {
		if (activeCameraIndex < cameraLabels.length - 1) {
			setActiveCameraIndex(activeCameraIndex + 1);
		}
	};

	const handlePrev = () => {
		if (activeCameraIndex > 0) {
			setActiveCameraIndex(activeCameraIndex - 1);
		}
	};

	return (
		<Container maxWidth="xl">
			<CssBaseline />
			<Paper elevation={3} className="w-full p-[5%] pt-[2%] mt-5">
				<div className="text-3xl text-text bg-slate-200 px-5 py-2">Setup Cameras Here:</div>
				<div className="mt-5 px-10">
					<div className="flex flex-row w-full">
						<AvailableWebcams
							label="leftTop"
							isActive={activeCameraIndex === 0}
							onNext={handleNext}
							onPrev={handlePrev}
							isFirst={activeCameraIndex === 0}
							isLast={activeCameraIndex === cameraLabels.length - 1}
						/>
						<AvailableWebcams
							label="rightTop"
							isActive={activeCameraIndex === 1}
							onNext={handleNext}
							onPrev={handlePrev}
							isFirst={activeCameraIndex === 0}
							isLast={activeCameraIndex === cameraLabels.length - 1}
						/>
					</div>
					<div className="flex flex-row w-full mt-16">
						<AvailableWebcams
							label="leftSide"
							isActive={activeCameraIndex === 2}
							onNext={handleNext}
							onPrev={handlePrev}
							isFirst={activeCameraIndex === 0}
							isLast={activeCameraIndex === cameraLabels.length - 1}
						/>
						<AvailableWebcams
							label="rightSide"
							isActive={activeCameraIndex === 3}
							onNext={handleNext}
							onPrev={handlePrev}
							isFirst={activeCameraIndex === 0}
							isLast={activeCameraIndex === cameraLabels.length - 1}
						/>
					</div>
				</div>
			</Paper>
		</Container>
	);
};

export default IntegratedComponent;
